import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import {
  useCreateOrderMutation,
  useResolveOrderItemsMutation,
  useScanOrderItemMutation,
  useSearchOrderItemsQuery,
} from "../api/api";
import SaleItemResultRow from "../components/Sales/SaleItemResultRow";
import { ORDER_COUNTRIES, ORDER_REGIONS } from "../data/orderRegions";
import { useTranslation } from "../hooks/useTranslation";
import OrderRecipientLookup from "./OrderRecipientLookup";
import {
  clearOrderDraftHandoff,
  hydrateHandoffLines,
  readOrderDraftHandoff,
  toOrderBasketLine,
} from "./orderDraftHandoff";
import { describeOrderApiError, logOrderApiError } from "./orderErrors";
import {
  buildWizardItemChips,
  feedbackAfterFinderInput,
  isOrderItemAddable,
  sortOrderSearchResults,
} from "./orderPresentation";
import {
  WIZARD_STEPS as STEPS,
  resolveWizardEntry,
  basketLinePriceError,
  resolveBasketUnitPrice,
  summarizeBasket,
  toRecipientFromCustomer,
} from "./orderWizardFlow";
import { formatMoney, useDebounce } from "./salesUtils";

const MIN_QUERY_LENGTH = 2;
// Mirrors the server's limit for `comments`; the note travels to ABEX.
const COURIER_NOTE_MAX_LENGTH = 2000;

const STEP_LABELS = {
  products: ["ordersWizard.stepProducts", "ordersWizard.stepProductsHint"],
  delivery: ["ordersWizard.stepDelivery", "ordersWizard.stepDeliveryHint"],
  review: ["ordersWizard.stepReview", "ordersWizard.stepReviewHint"],
};

const SOURCE_OPTIONS = [
  { value: "INSTAGRAM", labelKey: "storefrontOrders.channelInstagram" },
  { value: "FACEBOOK", labelKey: "storefrontOrders.channelFacebook" },
  { value: "PHONE", labelKey: "storefrontOrders.channelPhone" },
  { value: "OTHER", labelKey: "storefrontOrders.channelOther" },
];

const EMPTY_RECIPIENT = {
  recipientName: "",
  phone: "",
  country: "Kosove",
  city: "",
  address: "",
};

const fill = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    String(template),
  );

const validateRecipient = (recipient, t) => ({
  recipientName: recipient.recipientName.trim() ? null : t("ordersWizard.nameRequired"),
  phone: recipient.phone.trim() ? null : t("ordersWizard.phoneRequired"),
  city: recipient.city.trim() ? null : t("ordersWizard.cityRequired"),
  address: recipient.address.trim() ? null : t("ordersWizard.addressRequired"),
});

const hasErrors = (errors) => Object.values(errors).some(Boolean);

function describeHandoffIssue(issue, t) {
  switch (issue.type) {
    case "not_found":
      return fill(t("ordersWizard.handoffItemNotFound"), { id: issue.id });
    case "no_price":
      return fill(t("ordersWizard.handoffItemNoPrice"), { name: issue.name });
    case "reduced":
      return fill(t("ordersWizard.handoffItemReduced"), { name: issue.name, count: issue.count });
    default:
      return fill(t("ordersWizard.handoffItemNoStock"), { name: issue.name });
  }
}

/**
 * Orders v2 manual order wizard: Produktet -> Dërgesa & çmimi -> Përmbledhje.
 *
 * The workflow creates an order; it does not manage customers. The recipient is
 * entered (or prefilled from an existing customer) inside "Dërgesa & çmimi" and
 * belongs to this order only.
 *
 * Creating the order is the ONLY moment stock is reserved, and it never contacts
 * the courier: the shipment is requested later from the order page. Money is
 * recomputed server-side, so the totals here are a preview only.
 */
const CreateOrder = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scanInputRef = useRef(null);
  const mountedRef = useRef(true);
  const handoffStartedRef = useRef(false);

  // Decided once, on mount: Sales handoffs open at "Dërgesa & çmimi".
  const [entry] = useState(() =>
    resolveWizardEntry({
      fromSales: searchParams.get("from") === "sales",
      handedLines: readOrderDraftHandoff(),
    }),
  );

  const [stepIndex, setStepIndex] = useState(entry.stepIndex);
  const [recipient, setRecipient] = useState(EMPTY_RECIPIENT);
  const [prefilledFromCustomer, setPrefilledFromCustomer] = useState(false);
  const [orderChannel, setOrderChannel] = useState("INSTAGRAM");
  const [basket, setBasket] = useState([]);
  const [shippingFee, setShippingFee] = useState("2");
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState({});
  const [attemptedSteps, setAttemptedSteps] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [scanValue, setScanValue] = useState("");
  const [scanFeedback, setScanFeedback] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [handoff, setHandoff] = useState(() =>
    entry.handedLines.length
      ? { status: "loading", issues: [], count: 0 }
      : { status: "idle", issues: [], count: 0 },
  );

  const step = STEPS[stepIndex];

  const debouncedProductSearch = useDebounce(productSearch, 300).trim();
  // The product step starts empty: nothing is requested without a real query.
  const canSearchProducts = step === "products" && debouncedProductSearch.length >= MIN_QUERY_LENGTH;
  const { data: productResults = [], isFetching: isSearchingProducts } = useSearchOrderItemsQuery(
    { query: debouncedProductSearch, limit: 24 },
    { skip: !canSearchProducts },
  );

  const [scanOrderItem, { isLoading: isScanning }] = useScanOrderItemMutation();
  const [resolveOrderItems] = useResolveOrderItemsMutation();
  const [createOrder, { isLoading: isCreating }] = useCreateOrderMutation();

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (step === "products") {
      scanInputRef.current?.focus();
    }
  }, [step]);

  /**
   * Sales -> Orders handoff, run exactly once per mount.
   *
   * Only ids and quantities were handed over; product, price, stock and image are
   * re-read here. Nothing is reserved until "Krijo porosinë". Messages are stored
   * as issue codes and translated while rendering, so this effect never depends
   * on the (per-render) translation function.
   */
  useEffect(() => {
    if (handoffStartedRef.current) {
      return;
    }
    handoffStartedRef.current = true;

    if (entry.discardStalePayload) {
      clearOrderDraftHandoff();
    }

    const handedLines = entry.handedLines;
    if (!handedLines.length) {
      return;
    }

    resolveOrderItems(handedLines.map((line) => line.catalogVariantId))
      .unwrap()
      .then((result) => {
        const { lines, issues } = hydrateHandoffLines(handedLines, result?.items || []);
        clearOrderDraftHandoff();

        if (!mountedRef.current) {
          return;
        }

        if (lines.length) {
          setBasket(lines);
        }
        setHandoff({ status: "done", issues, count: lines.length });
      })
      .catch((error) => {
        // Keep the payload so a refresh can retry. Products are never dropped
        // silently: the failure is shown next to "Ndrysho produktet".
        logOrderApiError(error, "orders:handoff");
        if (mountedRef.current) {
          setHandoff({ status: "failed", issues: [], count: 0 });
        }
      });
  }, [entry, resolveOrderItems]);

  const { units: basketUnits, subtotal: productsSubtotal } = summarizeBasket(basket);
  const shippingFeeAmount = Math.round(Math.max(0, Number(shippingFee) || 0) * 100) / 100;
  const codPreview = productsSubtotal + shippingFeeAmount;
  const recipientErrors = validateRecipient(recipient, t);

  const stepIsValid = (name) => {
    if (name === "products") return basket.length > 0 && !basket.some((line) => basketLinePriceError(line));
    if (name === "delivery") return !hasErrors(recipientErrors);
    return true;
  };

  // Errors appear after the user leaves a field or tries to continue - never on
  // first render.
  const visibleError = (field) =>
    touched[field] || attemptedSteps.delivery ? recipientErrors[field] : null;

  const markTouched = (field) => setTouched((current) => ({ ...current, [field]: true }));

  const updateRecipient = (field, value) =>
    setRecipient((current) => ({ ...current, [field]: value }));

  const applyExistingCustomer = (customer) => {
    // A copy for this order only; the stored customer is never edited from here.
    setRecipient(toRecipientFromCustomer(customer));
    setPrefilledFromCustomer(true);
  };

  const addToBasket = (item) => {
    if (!item?.canAddManually || item.price == null) {
      return false;
    }

    const id = Number(item.catalogVariantId);
    const limit = Number(item.availableQuantity || 0);
    const existing = basket.find((line) => line.catalogVariantId === id);

    if (existing && limit && existing.quantity >= limit) {
      setScanFeedback({ color: "warning", message: t("ordersWizard.maxStockReached") });
      return false;
    }

    setBasket((current) =>
      current.some((line) => line.catalogVariantId === id)
        ? current.map((line) =>
            line.catalogVariantId === id
              ? { ...line, quantity: line.quantity + 1, availableQuantity: limit || line.availableQuantity }
              : line,
          )
        : [...current, toOrderBasketLine(item, 1)],
    );
    return true;
  };

  const changeQuantity = (catalogVariantId, delta) => {
    const line = basket.find((entryLine) => entryLine.catalogVariantId === catalogVariantId);
    if (!line) return;

    const next = line.quantity + delta;
    if (next < 1) return;

    if (line.availableQuantity && next > line.availableQuantity) {
      setScanFeedback({ color: "warning", message: t("ordersWizard.maxStockReached") });
      return;
    }

    setBasket((current) =>
      current.map((entryLine) =>
        entryLine.catalogVariantId === catalogVariantId ? { ...entryLine, quantity: next } : entryLine,
      ),
    );
  };

  const changePrice = (catalogVariantId, value) => {
    setBasket((current) =>
      current.map((line) => (line.catalogVariantId === catalogVariantId ? { ...line, finalPrice: value } : line)),
    );
  };

  const removeLine = (catalogVariantId) => {
    setBasket((current) => current.filter((line) => line.catalogVariantId !== catalogVariantId));
  };

  const handleScan = async (event) => {
    event.preventDefault();
    const barcode = scanValue.trim();
    if (!barcode) {
      return;
    }

    try {
      const result = await scanOrderItem(barcode).unwrap();

      if (result?.status === "ready" && result.item) {
        if (addToBasket(result.item)) {
          setScanFeedback({
            color: "success",
            message: fill(t("ordersWizard.scanAdded"), { name: result.item.productTitle }),
          });
        }
      } else if (result?.status === "blocked") {
        setScanFeedback({ color: "warning", message: t("ordersWizard.scanBlocked") });
      } else {
        setScanFeedback({ color: "warning", message: t("ordersWizard.scanNotFound") });
      }
    } catch (error) {
      logOrderApiError(error, "orders:scan");
      setScanFeedback({ color: "danger", message: describeOrderApiError(error, t) });
    } finally {
      setScanValue("");
      scanInputRef.current?.focus();
    }
  };

  const goToStep = (name) => {
    setSubmitError(null);
    setStepIndex(STEPS.indexOf(name));
  };

  const goNext = () => {
    if (!stepIsValid(step)) {
      setAttemptedSteps((current) => ({ ...current, [step]: true }));
      return;
    }

    setSubmitError(null);
    setStepIndex((current) => Math.min(STEPS.length - 1, current + 1));
  };

  const goBack = () => {
    setSubmitError(null);
    setStepIndex((current) => Math.max(0, current - 1));
  };

  const submitOrder = async () => {
    // Validate every step, not only the review screen.
    const invalidIndex = STEPS.findIndex((name) => !stepIsValid(name));
    if (invalidIndex !== -1) {
      setAttemptedSteps((current) => ({ ...current, [STEPS[invalidIndex]]: true }));
      setStepIndex(invalidIndex);
      return;
    }

    setSubmitError(null);

    try {
      const result = await createOrder({
        recipientName: recipient.recipientName.trim(),
        phone: recipient.phone.trim(),
        country: recipient.country || "Kosove",
        city: recipient.city.trim(),
        address: recipient.address.trim(),
        comments: note.trim() || undefined,
        orderChannel,
        shippingFeeAmount,
        // Ids and quantities (plus a discounted price when staff set one): the server
        // re-validates the price and reserves stock.
        products: basket.map((line) => ({
          catalogVariantId: line.catalogVariantId,
          quantity: line.quantity,
          ...(resolveBasketUnitPrice(line) < line.price
            ? { finalUnitPriceAmount: resolveBasketUnitPrice(line) }
            : {}),
        })),
      }).unwrap();

      clearOrderDraftHandoff();
      const orderId = result?.orderId || result?.id;
      navigate(orderId ? `/admin/orders?orderId=${orderId}` : "/admin/orders");
    } catch (error) {
      logOrderApiError(error, "orders:create");
      setSubmitError(describeOrderApiError(error, t, "ordersErrors.createFailed"));
    }
  };

  const renderChips = (item) => {
    const chips = buildWizardItemChips(item, t);
    return chips.length ? (
      <span className="ow-attrs">
        {chips.map((chip) => (
          <span key={chip} className="ow-attr">
            {chip}
          </span>
        ))}
      </span>
    ) : null;
  };

  const renderImage = (url, className, alt = "") =>
    url ? (
      <img className={className} src={url} alt={alt} loading="lazy" />
    ) : (
      <span className={`${className} ${className}--empty`} aria-hidden="true" />
    );

  const renderTotals = ({ shippingLabelKey = "storefrontOrders.shippingFee" } = {}) => (
    <div className="ow-summary">
      <div className="ow-summary-row">
        <span>{t("storefrontOrders.productsSubtotal")}</span>
        <span>{formatMoney(productsSubtotal)}</span>
      </div>
      <div className="ow-summary-row">
        <span>{t(shippingLabelKey)}</span>
        <span>{formatMoney(shippingFeeAmount)}</span>
      </div>
      <div className="ow-cod-box">
        <span>{t("storefrontOrders.codTotal")}</span>
        <span>{formatMoney(codPreview)}</span>
      </div>
    </div>
  );

  const renderProductResults = () => {
    if (!canSearchProducts) {
      return (
        <div className="ow-picker-empty">
          <i className="tim-icons icon-zoom-split" aria-hidden="true" />
          <p className="mb-0">{t("ordersWizard.pickerIntroHint")}</p>
        </div>
      );
    }

    if (isSearchingProducts) {
      return <p className="ow-results-state">{t("ordersWizard.searching")}</p>;
    }

    if (!productResults.length) {
      return <p className="ow-results-state">{t("ordersUi.noProductFound")}</p>;
    }

    return (
      <>
        <div className="ow-results-head">
          <span>{t("ordersUi.searchResults")}</span>
          <span>{fill(t("ordersUi.productsFound"), { count: productResults.length })}</span>
        </div>
        <div className="sir-list ow-results">
          {sortOrderSearchResults(productResults).map((item) => {
            const hasPrice = item.price != null;
            const inStock = Number(item.availableQuantity || 0) > 0;
            const addable = isOrderItemAddable(item);

            return (
              <SaleItemResultRow
                key={item.catalogVariantId}
                item={item}
                title={item.productTitle}
                chips={buildWizardItemChips(item, t)}
                sku={item.code || null}
                price={hasPrice ? formatMoney(item.price) : "—"}
                stockLabel={
                  !hasPrice
                    ? t("ordersWizard.noPrice")
                    : inStock
                      ? fill(t("ordersUi.inStockCount"), { count: item.availableQuantity })
                      : t("ordersUi.noStock")
                }
                stockTone={hasPrice && inStock ? "ok" : "empty"}
                actionLabel={t("ordersWizard.addProduct")}
                isAddable={addable}
                doubleClickHint={t("ordersUi.doubleClickHint")}
                // Same handler as the button: stock and price rules apply identically.
                onAdd={addToBasket}
              />
            );
          })}
        </div>
      </>
    );
  };

  const renderProductsSummary = () => {
    const [firstLine] = basket;
    return (
      <div className="ow-products-summary">
        {firstLine ? renderImage(firstLine.imageUrl, "ow-summary-image") : null}
        <div className="ow-products-summary-text">
          <strong>
            {handoff.status === "loading"
              ? t("ordersWizard.handoffLoading")
              : fill(t(basketUnits === 1 ? "ordersUi.productsSummaryOne" : "ordersWizard.productsSummary"), {
                  count: basketUnits,
                  total: formatMoney(productsSubtotal),
                })}
          </strong>
          {firstLine ? (
            <span>
              {[
                firstLine.productTitle,
                firstLine.size ? `${t("ordersWizard.sizeLabel")} ${firstLine.size}` : null,
                `×${firstLine.quantity}`,
              ]
                .filter(Boolean)
                .join(" · ")}
              {basket.length > 1 ? ` · +${basket.length - 1}` : ""}
            </span>
          ) : null}
        </div>
        <Button color="secondary" outline onClick={() => goToStep("products")}>
          {t("ordersWizard.changeProducts")}
        </Button>
      </div>
    );
  };

  const renderField = ({ id, field, labelKey, type = "text", required = true, extra = {} }) => (
    <FormGroup className={extra.className}>
      <Label for={id} className="ow-field-label">
        {t(labelKey)}
        {required ? " *" : ""}
      </Label>
      <Input
        id={id}
        type={type}
        autoComplete="off"
        rows={extra.rows}
        list={extra.list}
        value={recipient[field]}
        invalid={Boolean(visibleError(field))}
        onBlur={() => markTouched(field)}
        onChange={(event) => updateRecipient(field, event.target.value)}
      />
      <FormFeedback>{visibleError(field)}</FormFeedback>
    </FormGroup>
  );

  const cities = ORDER_REGIONS[recipient.country] || [];
  const sourceLabel = t(
    SOURCE_OPTIONS.find((option) => option.value === orderChannel)?.labelKey || "storefrontOrders.channelOther",
  );

  return (
    <div className="content sales-premium orders-wizard-page">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header ow-header">
          <div>
            <div className="sp-title">{t("ordersWizard.title")}</div>
            <p className="sp-subtitle">{t("ordersWizard.subtitle")}</p>
          </div>
          <Button color="secondary" outline onClick={() => navigate("/admin/orders")}>
            {t("ordersWizard.cancel")}
          </Button>
        </CardHeader>

        <CardBody className="sales-page-body">
          <ol className="ow-steps ow-steps--three">
            {STEPS.map((name, index) => (
              <li
                key={name}
                className={`ow-step${index === stepIndex ? " is-active" : ""}${index < stepIndex ? " is-done" : ""}`}
                aria-current={index === stepIndex ? "step" : undefined}
              >
                <span className="ow-step-index">{index < stepIndex ? "✓" : index + 1}</span>
                <span className="ow-step-text">
                  <span className="ow-step-title">{t(STEP_LABELS[name][0])}</span>
                  <span className="ow-step-hint">{t(STEP_LABELS[name][1])}</span>
                </span>
              </li>
            ))}
          </ol>

          {handoff.status === "failed" ? <Alert color="warning">{t("ordersWizard.handoffFailed")}</Alert> : null}
          {handoff.status === "done" && handoff.issues.length ? (
            <Alert
              color="warning"
              className="ow-handoff"
              toggle={() => setHandoff((current) => ({ ...current, issues: [] }))}
            >
              <ul>
                {handoff.issues.map((issue) => (
                  <li key={`${issue.type}-${issue.id}`}>{describeHandoffIssue(issue, t)}</li>
                ))}
              </ul>
            </Alert>
          ) : null}

          {step === "products" ? (
            <div className="ow-grid ow-grid--products">
              <section className="ow-panel">
                <div className="ow-panel-head">
                  <h3 className="ow-panel-title">{t("ordersWizard.pickerIntroTitle")}</h3>
                  <span className="ow-panel-hint">
                    <i className="tim-icons icon-alert-circle-exc" aria-hidden="true" />{" "}
                    {t("ordersUi.doubleClickHint")}
                  </span>
                </div>

                <div className="ow-finder">
                  <form className="ow-finder-field" onSubmit={handleScan}>
                    <Label for="ow-scan" className="ow-field-label">
                      {t("ordersUi.scanLabel")}
                    </Label>
                    <div className="ow-input-icon">
                      <i className="tim-icons icon-tablet-2" aria-hidden="true" />
                      <Input
                        id="ow-scan"
                        innerRef={scanInputRef}
                        type="text"
                        autoComplete="off"
                        className="ow-scan-input"
                        placeholder={t("ordersUi.scanPlaceholder")}
                        value={scanValue}
                        onChange={(event) => {
                          setScanValue(event.target.value);
                          setScanFeedback((current) => feedbackAfterFinderInput(current, event.target.value));
                        }}
                        disabled={isScanning}
                      />
                    </div>
                  </form>
                  <span className="ow-finder-or">{t("ordersUi.or")}</span>
                  <div className="ow-finder-field">
                    <Label for="ow-product-search" className="ow-field-label">
                      {t("ordersUi.searchLabel")}
                    </Label>
                    <div className="ow-input-icon">
                      <i className="tim-icons icon-zoom-split" aria-hidden="true" />
                      <Input
                        id="ow-product-search"
                        type="search"
                        autoComplete="off"
                        placeholder={t("ordersWizard.searchProducts")}
                        value={productSearch}
                        onChange={(event) => {
                          setProductSearch(event.target.value);
                          // A barcode message must not linger over manual search results.
                          setScanFeedback((current) => feedbackAfterFinderInput(current, event.target.value));
                        }}
                      />
                    </div>
                  </div>
                </div>

                {scanFeedback ? (
                  <Alert color={scanFeedback.color} className="ow-scan-feedback">
                    {scanFeedback.message}
                  </Alert>
                ) : null}

                <div className="ow-products">{renderProductResults()}</div>
              </section>

              <section className="ow-panel">
                <div className="ow-panel-head">
                  <h3 className="ow-panel-title">
                    {t("ordersWizard.orderProducts")} ({basket.length})
                  </h3>
                </div>
                {basket.length ? (
                  <div className="ow-basket">
                    {basket.map((line) => (
                      <div key={line.catalogVariantId} className="ow-basket-line">
                        {renderImage(line.imageUrl, "ow-basket-image")}
                        <div className="ow-basket-info">
                          <span className="ow-line-title">{line.productTitle}</span>
                          {renderChips(line)}
                          {line.code ? <span className="ow-sku">SKU {line.code}</span> : null}
                          <div className="d-flex align-items-center" style={{ gap: 6 }}>
                            <Input
                              type="number"
                              inputMode="decimal"
                              min="0"
                              step="0.01"
                              max={line.price}
                              bsSize="sm"
                              style={{ width: 96 }}
                              aria-label={`${t("storefrontOrders.finalPrice")} · ${line.productTitle}`}
                              value={line.finalPrice ?? ""}
                              invalid={Boolean(basketLinePriceError(line))}
                              onChange={(event) => changePrice(line.catalogVariantId, event.target.value)}
                            />
                            <span>€</span>
                            {resolveBasketUnitPrice(line) < line.price ? (
                              <span className="ov-price-tag">
                                {t("storefrontOrders.priceOverridden")} · {formatMoney(line.price)}
                              </span>
                            ) : null}
                          </div>
                          {basketLinePriceError(line) ? (
                            <span className="text-danger small">{t(basketLinePriceError(line))}</span>
                          ) : null}
                        </div>
                        <div className="ow-qty">
                          <button
                            type="button"
                            aria-label={t("ordersUi.decreaseQuantity")}
                            disabled={line.quantity <= 1}
                            onClick={() => changeQuantity(line.catalogVariantId, -1)}
                          >
                            −
                          </button>
                          <span>{line.quantity}</span>
                          <button
                            type="button"
                            aria-label={t("ordersUi.increaseQuantity")}
                            disabled={Boolean(line.availableQuantity) && line.quantity >= line.availableQuantity}
                            onClick={() => changeQuantity(line.catalogVariantId, 1)}
                          >
                            +
                          </button>
                        </div>
                        <span className="ow-basket-total">{formatMoney(resolveBasketUnitPrice(line) * line.quantity)}</span>
                        <button
                          type="button"
                          className="ow-remove"
                          aria-label={`${t("ordersWizard.remove")} · ${line.productTitle}`}
                          title={t("ordersWizard.remove")}
                          onClick={() => removeLine(line.catalogVariantId)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ow-picker-empty">
                    <i className="tim-icons icon-bag-16" aria-hidden="true" />
                    <p className="mb-0">{t("ordersWizard.basketEmpty")}</p>
                  </div>
                )}
                {attemptedSteps.products && !basket.length ? (
                  <p className="ow-field-error">{t("ordersWizard.productsRequired")}</p>
                ) : null}
              </section>
            </div>
          ) : null}

          {step === "delivery" ? (
            <>
              {renderProductsSummary()}

              <div className="ow-grid ow-grid--delivery">
                <section className="ow-panel">
                  <div className="ow-section">
                    <h3 className="ow-panel-title">{t("ordersWizard.recipientSection")}</h3>
                    <p className="ow-panel-subtitle">{t("ordersUi.recipientSubtitle")}</p>
                    <OrderRecipientLookup t={t} onSelect={applyExistingCustomer} />
                  </div>

                  <div className="ow-section">
                    <h4 className="ow-subtitle">{t("ordersUi.customerDetails")}</h4>
                    {prefilledFromCustomer ? (
                      <p className="ow-prefilled-note">{t("ordersWizard.recipientPrefilled")}</p>
                    ) : null}

                    <div className="ow-fields">
                      {renderField({ id: "ow-name", field: "recipientName", labelKey: "ordersWizard.customerName" })}
                      {renderField({ id: "ow-phone", field: "phone", labelKey: "ordersWizard.customerPhone", type: "tel" })}
                      <FormGroup>
                        <Label for="ow-country" className="ow-field-label">
                          {t("ordersWizard.customerCountry")}
                        </Label>
                        <Input
                          id="ow-country"
                          type="select"
                          value={recipient.country}
                          onChange={(event) =>
                            setRecipient((current) => ({ ...current, country: event.target.value, city: "" }))
                          }
                        >
                          {ORDER_COUNTRIES.map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                      {renderField({
                        id: "ow-city",
                        field: "city",
                        labelKey: "ordersWizard.customerCity",
                        extra: { list: "ow-city-options" },
                      })}
                      {renderField({
                        id: "ow-address",
                        field: "address",
                        labelKey: "ordersWizard.customerAddress",
                        type: "textarea",
                        extra: { rows: 2, className: "ow-fields-wide" },
                      })}
                    </div>
                    <datalist id="ow-city-options">
                      {cities.slice(0, 400).map((city) => (
                        <option key={city} value={city} />
                      ))}
                    </datalist>
                  </div>

                  <div className="ow-section">
                    <h4 className="ow-subtitle">{t("ordersWizard.sourceSection")}</h4>
                    <div className="ow-sources">
                      {SOURCE_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`ow-source${orderChannel === option.value ? " is-active" : ""}`}
                          aria-pressed={orderChannel === option.value}
                          onClick={() => setOrderChannel(option.value)}
                        >
                          {orderChannel === option.value ? (
                            <i className="tim-icons icon-check-2" aria-hidden="true" />
                          ) : null}
                          {t(option.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                </section>

                <section className="ow-panel">
                  <div className="ow-section">
                    <h3 className="ow-panel-title">{t("ordersWizard.abexSection")}</h3>
                    <div className="ow-abex">
                      <span className="ow-abex-icon" aria-hidden="true">
                        <i className="tim-icons icon-delivery-fast" />
                      </span>
                      <div>
                        <strong>ABEX</strong>
                        <p>{t("ordersWizard.abexOnly")}</p>
                      </div>
                    </div>
                    <FormGroup className="mb-0">
                      <Label for="ow-shipping" className="ow-field-label">
                        {t("ordersUi.shippingCostCod")}
                      </Label>
                      <div className="ow-money-input">
                        <Input
                          id="ow-shipping"
                          type="number"
                          min="0"
                          step="0.01"
                          value={shippingFee}
                          onChange={(event) => setShippingFee(event.target.value)}
                        />
                        <span className="ow-money-suffix" aria-hidden="true">
                          €
                        </span>
                      </div>
                      <p className="ow-field-hint">{t("ordersWizard.shippingHint")}</p>
                    </FormGroup>
                  </div>

                  <div className="ow-section">
                    <h3 className="ow-panel-title">{t("ordersUi.paymentSummary")}</h3>
                    {renderTotals({ shippingLabelKey: "ordersUi.shippingAbex" })}
                    <div className="ow-note ow-note--warn">
                      <i className="tim-icons icon-alert-circle-exc" aria-hidden="true" />
                      <div>
                        <strong>{t("ordersWizard.nothingSentYet")}</strong>
                        <p>{t("ordersWizard.nothingSentYetHint")}</p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </>
          ) : null}

          {step === "review" ? (
            <div className="ow-grid ow-grid--review">
              <section className="ow-panel">
                <h3 className="ow-panel-title">{t("ordersWizard.recipientSection")}</h3>
                <dl className="ow-review-list">
                  <div className="ow-review-item">
                    <span className="ow-review-icon" aria-hidden="true">
                      <i className="tim-icons icon-single-02" />
                    </span>
                    <div>
                      <dt>{t("ordersUi.fullName")}</dt>
                      <dd>{recipient.recipientName}</dd>
                    </div>
                  </div>
                  <div className="ow-review-item">
                    <span className="ow-review-icon" aria-hidden="true">
                      <i className="tim-icons icon-mobile" />
                    </span>
                    <div>
                      <dt>{t("ordersWizard.customerPhone")}</dt>
                      <dd>{recipient.phone}</dd>
                    </div>
                  </div>
                  <div className="ow-review-item">
                    <span className="ow-review-icon" aria-hidden="true">
                      <i className="tim-icons icon-square-pin" />
                    </span>
                    <div>
                      <dt>{t("ordersUi.fullAddress")}</dt>
                      <dd>{[recipient.address, recipient.city, recipient.country].filter(Boolean).join(", ")}</dd>
                    </div>
                  </div>
                  <div className="ow-review-item">
                    <span className="ow-review-icon" aria-hidden="true">
                      <i className="tim-icons icon-link-72" />
                    </span>
                    <div>
                      <dt>{t("ordersWizard.orderSource")}</dt>
                      <dd>{sourceLabel}</dd>
                    </div>
                  </div>
                </dl>

                <FormGroup className="ow-courier-note mb-0">
                  <Label for="ow-note" className="ow-field-label">
                    {t("ordersUi.courierNoteOptional")}
                  </Label>
                  <Input
                    id="ow-note"
                    type="textarea"
                    rows="3"
                    maxLength={COURIER_NOTE_MAX_LENGTH}
                    placeholder={t("ordersUi.courierNotePlaceholder")}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                  />
                  <div className="ow-note-meta">
                    <span>{t("ordersUi.courierNoteHint")}</span>
                    <span>
                      {note.length}/{COURIER_NOTE_MAX_LENGTH}
                    </span>
                  </div>
                </FormGroup>
              </section>

              <section className="ow-panel">
                <h3 className="ow-panel-title">{t("ordersWizard.orderProducts")}</h3>
                <div className="ow-basket">
                  {basket.map((line) => (
                    <div key={line.catalogVariantId} className="ow-basket-line ow-basket-line--review">
                      {renderImage(line.imageUrl, "ow-basket-image")}
                      <div className="ow-basket-info">
                        <span className="ow-line-title">{line.productTitle}</span>
                        {renderChips(line)}
                      </div>
                      <span className="ow-review-qty">×{line.quantity}</span>
                      <span className="ow-basket-total">{formatMoney(resolveBasketUnitPrice(line) * line.quantity)}</span>
                    </div>
                  ))}
                </div>

                {renderTotals()}

                <div className="ow-note ow-note--info">
                  <i className="tim-icons icon-bulb-63" aria-hidden="true" />
                  <div>
                    <strong>{t("ordersWizard.afterCreation")}</strong>
                    <ul className="ow-checklist">
                      <li>{t("ordersWizard.afterCreationStock")}</li>
                      <li>{t("ordersWizard.afterCreationInbox")}</li>
                      <li>{t("ordersWizard.afterCreationNoAbex")}</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          ) : null}

          {submitError ? <Alert color="danger" className="mt-3">{submitError}</Alert> : null}

          <div className="ow-footer">
            <Button color="secondary" outline disabled={stepIndex === 0} onClick={goBack}>
              ← {t("ordersWizard.back")}
            </Button>

            {step === "review" ? (
              <Button color="primary" className="ow-cta" disabled={isCreating} onClick={submitOrder}>
                {isCreating ? t("ordersWizard.creating") : t("ordersWizard.createOrder")}
              </Button>
            ) : (
              <Button color="primary" className="ow-cta" onClick={goNext}>
                {t("ordersWizard.continue")} →
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateOrder;
